import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Upload, Building, MapPin } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { selectCurrentProject, selectCurrentSite, selectInstanceInfo, selectMenuBadges } from '@/entities/layout';
import { exportConsignments } from '@/store/slices/consignmentSlice';
import { HeaderSearch, FilterTrigger, HeaderSlot } from '@/features/header-filters';

import { useAppDispatch, usePermissions, useProjectSiteParams } from '@/shared/lib';
import { MANUAL_NODES } from '@/shared/config/manual';
import { HelpLink, LanguageSelector } from '@/shared/ui';

interface HeaderProps {
  consignmentCount: number;
}

export const Header: React.FC<HeaderProps> = ({ consignmentCount }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { projectId, siteId } = useProjectSiteParams();
  const location = useLocation();

  const isDashboard = location.pathname.includes('/dashboard');

  // Create memoized selectors for current projectId and siteId
  const currentProjectSelector = useMemo(
    () => (projectId && siteId ? selectCurrentProject(projectId, siteId) : () => null),
    [projectId, siteId]
  );

  const currentSiteSelector = useMemo(
    () => (projectId && siteId ? selectCurrentSite(projectId, siteId) : () => null),
    [projectId, siteId]
  );

  const instanceInfoSelector = useMemo(
    () => (projectId && siteId ? selectInstanceInfo(projectId, siteId) : () => null),
    [projectId, siteId]
  );

  const badgesSelector = useMemo(
    () => (projectId && siteId ? selectMenuBadges(projectId, siteId) : () => null),
    [projectId, siteId]
  );

  const currentProject = useSelector(currentProjectSelector);
  const currentSite = useSelector(currentSiteSelector);
  const instanceInfo = useSelector(instanceInfoSelector);
  const badges = useSelector(badgesSelector);
  const { canExportData, isReadOnly } = usePermissions();

  const handleExport = () => {
    dispatch(exportConsignments());
  };

  return (
    <header className="flex w-full justify-between items-center shrink-0 border-b border-border bg-background px-6 py-4 max-lg:flex-col max-lg:gap-4 max-lg:p-4">
      {/* Project and context section */}
      <div className="flex flex-col items-start gap-1 shrink-0 max-lg:w-full">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-foreground text-lg font-semibold leading-tight">
              {currentProject?.name || instanceInfo?.name || 'RITA'}
            </h1>
          </div>
          {currentSite && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <MapPin className="w-3 h-3" />
              <span className="text-sm">{currentSite.name}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            {badges && (badges.serviceRequests > 0 || badges.releaseOrders > 0) && (
              <div className="flex gap-1">
                {badges.serviceRequests > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    SRF: {badges.serviceRequests}
                  </Badge>
                )}
                {badges.releaseOrders > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    RO: {badges.releaseOrders}
                  </Badge>
                )}
              </div>
            )}
            {isReadOnly && (
              <Badge variant="outline" className="text-xs border-amber-500 text-amber-700">
                Read Only
              </Badge>
            )}
          </div>
        </div>
        {!isDashboard && (
          <p className="text-muted-foreground text-xs font-medium">
            {t('navigation.consignments', { count: consignmentCount })}
          </p>
        )}
      </div>

      {!isDashboard && <HeaderSearch />}

      {/* Actions */}
      <div className="flex items-center gap-2 max-lg:w-full max-lg:justify-between max-sm:flex-wrap">
        <LanguageSelector />

        <HelpLink target={MANUAL_NODES.GLOBAL_HELP} />

        <HeaderSlot name="actions" />

        {!isDashboard && <FilterTrigger />}

        {!isDashboard && canExportData && (
          <Button variant="secondary" className="h-9 gap-2" onClick={handleExport}>
            <Upload className="w-4 h-4" />
            <span className="max-sm:hidden">{t('common.export.label')}</span>
          </Button>
        )}
      </div>
    </header>
  );
};
